import { useCallback, useEffect } from 'react';
import { SnfCanvasRoutes } from './constants';
import {
  useCanvasNavigationTabDisplayMode,
  useIsZoomLocked,
  useSetCanvasNavigationTapDisplayMode,
} from 'redux/hooks/common';
import { DisplayMode } from 'components/Experiment/constants';
import { isDropModeUrl } from 'utils/Global';
import {
  useCurrentPageSearchResults,
  useIsDirtySearchDetails,
  useIsSavingSearch,
  useSaveSearch,
  useSearchSelectedRowsIndices,
} from 'redux/hooks/searchnFilter';
import { ArrowKeys, KeyboardKeys } from 'constants/';
import { useRMDispatch } from 'redux/hooks';
import { setSelectedRowIndices } from 'redux/reducers/searchnFilterReducer';
import { snfCanvasPadding } from 'components/searchAndFilter/canvas/constants';
import { useCanvasRectangleSize } from 'components/searchAndFilter/canvas/hooks';
import { TotalColumns } from 'components/canvas/constants';

const navigateGrid = (
  currentIndex: number,
  key: KeyboardKeys,
  totalCols: number,
  totalItems: number,
): number => {
  const totalRows = Math.ceil(totalItems / totalCols);
  let row = Math.floor(currentIndex / totalCols);
  const col = currentIndex % totalCols;

  switch (key) {
    case KeyboardKeys.ArrowUp:
      row = (row - 1 + totalRows) % totalRows;
      break;

    case KeyboardKeys.ArrowDown:
      row = (row + 1) % totalRows;
      break;

    case KeyboardKeys.ArrowLeft:
      if (currentIndex === 0) {
        return totalItems - 1;
      }
      return currentIndex - 1;

    case KeyboardKeys.ArrowRight:
      if (currentIndex === totalItems - 1) {
        return 0;
      }
      return currentIndex + 1;
  }

  let newIndex = row * totalCols + col;

  if (newIndex >= totalItems) {
    newIndex = totalItems - 1;
    if (row !== totalRows - 1) {
      newIndex = row * totalCols + (totalItems % totalCols || totalCols) - 1;
    }
  }

  return newIndex;
};

export const useSnfKeyboardHandler = (zoomInvoker: (scale: number) => void) => {
  const canvasTabDisplayMode = useCanvasNavigationTabDisplayMode();
  const setNavigateDisplayMode = useSetCanvasNavigationTapDisplayMode();
  const saveSearch = useSaveSearch();
  const isSavingSearch = useIsSavingSearch();
  const isDirty = useIsDirtySearchDetails();
  const isZoomLocked = useIsZoomLocked();
  const selectedRowsIndices = useSearchSelectedRowsIndices();
  const currentPageSearchResults = useCurrentPageSearchResults();
  const dispatch = useRMDispatch();
  const rectangleSize = useCanvasRectangleSize();

  const handleUserKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const isCanvasRoute = window.location.pathname.includes('canvas');
      const isDropMode = isDropModeUrl();
      const ctrlKey = e.ctrlKey || e.metaKey;
      const { key } = e;

      const canZoom = !isZoomLocked || !isDropMode;

      if (ctrlKey && key === KeyboardKeys.S) {
        e.preventDefault();
        if (!isSavingSearch && isDirty) {
          saveSearch();
        }
      } else if (ctrlKey && key === KeyboardKeys.Plus) {
        e.preventDefault();
        if (canZoom) {
          zoomInvoker(1);
        }
      } else if (ctrlKey && key === KeyboardKeys.Minus) {
        e.preventDefault();
        if (canZoom) {
          zoomInvoker(-1);
        }
      } else if (ctrlKey && key === KeyboardKeys.Zero) {
        e.preventDefault();
        if (canZoom) {
          zoomInvoker(0);
        }
      } else if (key === KeyboardKeys.F4 && isCanvasRoute) {
        e.preventDefault();
        const currentIndex = SnfCanvasRoutes.indexOf(canvasTabDisplayMode as DisplayMode);
        const newIndex = (currentIndex + 1) % SnfCanvasRoutes.length;
        setNavigateDisplayMode(SnfCanvasRoutes[newIndex]);
      } else if (ArrowKeys.includes(key as KeyboardKeys) && isCanvasRoute) {
        e.preventDefault();
        if (!currentPageSearchResults?.length) {
          return;
        }
        const nextIndex = navigateGrid(
          selectedRowsIndices[0],
          key as KeyboardKeys,
          TotalColumns,
          currentPageSearchResults?.length,
        );
        dispatch(setSelectedRowIndices([nextIndex]));

        const container = document.getElementById(
          'canvas-scroll-container',
        ) as HTMLDivElement | null;
        if (container) {
          const scale = parseFloat(container.dataset.scale || '1') || 1;
          const col = nextIndex % TotalColumns;
          const row = Math.floor(nextIndex / TotalColumns);
          const centerX = (col * rectangleSize + rectangleSize / 2) * scale;
          const centerY = (row * rectangleSize + rectangleSize / 2) * scale;
          const targetLeft = snfCanvasPadding + centerX - container.clientWidth / 2;
          const targetTop = snfCanvasPadding + centerY - container.clientHeight / 2;
          const maxScrollLeft = container.scrollWidth - container.clientWidth;
          const maxScrollTop = container.scrollHeight - container.clientHeight;
          container.scrollLeft = Math.max(0, Math.min(targetLeft, maxScrollLeft));
          container.scrollTop = Math.max(0, Math.min(targetTop, maxScrollTop));
        }
      }
    },
    [
      saveSearch,
      isSavingSearch,
      isDirty,
      isZoomLocked,
      zoomInvoker,
      canvasTabDisplayMode,
      setNavigateDisplayMode,
    ],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => handleUserKeyPress(e);

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleUserKeyPress]);
};
